// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useI18n } from "@/i18n"
import { RequiredMark } from "./RequiredMark"

interface FreeTextListOrNoneProps {
  label: string
  placeholder: string
  noneLabel: string
  /**
   * `undefined` means "not answered yet" — renders with nothing selected,
   * including the "None" chip, so an unanswered field never looks like an
   * explicit choice was already made (item 30). `onChange` always reports a
   * real `explicitListOrNone` value once the user acts.
   */
  value: string[] | "none" | undefined
  onChange: (next: string[] | "none") => void
  inputId: string
  /** Defaults true — most explicit-list-or-none fields require SOME answer
   * (an explicit "none" still counts). Pass `false` for the rare field that
   * is genuinely optional even unanswered (e.g. Step13's medications). */
  required?: boolean
}

/**
 * Free-text tag entry for `explicitListOrNone(string)` fields with no
 * fixed vocabulary (likes/dislikes/never-eat/intolerances) — same
 * "explicit none, never a silent skip" contract as ChipMultiSelect, just
 * for user-typed values instead of a fixed chip set.
 */
export function FreeTextListOrNone({
  label,
  placeholder,
  noneLabel,
  value,
  onChange,
  inputId,
  required = true,
}: FreeTextListOrNoneProps) {
  const { t } = useI18n()
  const [draft, setDraft] = useState("")
  const items = value === "none" || value === undefined ? [] : value
  const isNone = value === "none"

  function addDraft() {
    const trimmed = draft.trim()
    if (!trimmed) return
    onChange([...items, trimmed])
    setDraft("")
  }

  function removeItem(item: string) {
    const next = items.filter(i => i !== item)
    onChange(next.length === 0 ? "none" : next)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-1">
        <label htmlFor={inputId} className="text-sm font-medium text-ink">
          {label}
        </label>
        {required && <RequiredMark />}
      </div>
      <div className="flex gap-2">
        <Input
          id={inputId}
          placeholder={placeholder}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault()
              addDraft()
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          className="min-h-11 shrink-0"
          disabled={!draft.trim()}
          onClick={addDraft}
          aria-label={label}
        >
          +
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map(item => (
          <span
            key={item}
            className="inline-flex items-center gap-1 rounded-full border border-line bg-paper px-3 py-1 text-xs text-ink"
          >
            {item}
            <button
              type="button"
              aria-label={`${t("common.dismiss")} ${item}`}
              onClick={() => removeItem(item)}
              className="text-ink-2 hover:text-destructive"
            >
              ×
            </button>
          </span>
        ))}
        <button
          type="button"
          aria-pressed={isNone}
          onClick={() => onChange("none")}
          className={cn(
            "min-h-8 rounded-full border px-3 py-1 text-xs font-semibold transition-colors",
            isNone
              ? "border-ink bg-ink text-paper"
              : "border-line bg-paper text-ink hover:border-ink/60"
          )}
        >
          {noneLabel}
        </button>
      </div>
    </div>
  )
}
